/**
 * Der Bestaetigungsdialog bei einem SHA-256-Treffer beim Dokumenten-Upload (Auftrag
 * "Duplikaterkennung beim Dokumenten-Upload per SHA-256", 07.09.2026): dieselbe Datei (Byte fuer
 * Byte, ueber den Hash geprueft) liegt bereits IRGENDWO im System vor - global ueber alle
 * Bestellungen, nicht nur bei dieser. Genau ZWEI Aktionen, wie im Auftrag festgelegt:
 *
 *   - Bestaetigen: verknuepft das VORHANDENE Dokument (`DokumenteTab.tsx#duplikatBestaetigen` ->
 *     `POST .../documents/:docType/link`) - laedt NICHTS neu hoch.
 *   - Abbrechen: verwirft NUR diese eine Datei. Im Multi-Upload ("Sonstige Dokumente") laufen die
 *     uebrigen Dateien der Schleife unveraendert weiter - jede hat ihre eigene, unabhaengige Pruefung.
 *
 * Eine dritte Option ("trotzdem als neue Kopie hochladen") gibt es laut Auftrag bewusst nicht.
 *
 * Wiederverwendet dasselbe Overlay-/Portal-Muster wie `UeberschreibDialog.tsx` (dort der
 * ausfuehrliche Kopfkommentar zu `createPortal(..., document.body)`) statt ein neues zu erfinden -
 * derselbe Wunsch steht so im Auftrag ("Bestehende Dialog-/Modal-Patterns im Frontend verwenden,
 * falls vorhanden").
 */
import { createPortal } from "react-dom";
import type { DokumentPerHashTreffer } from "../../../api/dokumente.ts";
import { fmtDT } from "../../../lib/format.ts";
import { Ikone } from "../../Ikone.tsx";

export interface DuplikatDialogProps {
  /** Beschriftung des Dokumenttyps, den DIESE Bestellung gerade hochladen wollte (`docType.label`). */
  typLabel: string;
  /** Der Server-Treffer (`GET /api/documents/by-hash/:sha256`). */
  treffer: DokumentPerHashTreffer;
  /**
   * Baut die Anzeigezeile fuer EINEN Eintrag aus `treffer.links` - liegt beim Aufrufer
   * (`DokumenteTab.tsx`), weil die Aufloesung von `orderId`/`docType` auf lesbare Bezeichnungen
   * `bestellungen` braucht, das dieser Dialog selbst nicht kennt.
   */
  linkBeschriftung: (link: DokumentPerHashTreffer["links"][number]) => string;
  onAbbrechen: () => void;
  onBestaetigen: () => void;
}

export function DuplikatDialog({
  typLabel,
  treffer,
  linkBeschriftung,
  onAbbrechen,
  onBestaetigen,
}: DuplikatDialogProps) {
  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section
        className="board-editor-dialog overwrite-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dokumentDuplikatTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="dokumentDuplikatTitel">Datei liegt bereits vor</h2>
            <p>
              „{treffer.originalName}" ({typLabel}) liegt bereits im System vor, hochgeladen am{" "}
              {fmtDT(treffer.hochgeladenAm)} - die Datei wird nicht erneut hochgeladen, sondern nur mit
              dieser Bestellung verknüpft.
            </p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body overwrite-body">
          <div className="overwrite-current">
            <strong>Bereits hinterlegt bei</strong>
            {treffer.links.length ? (
              treffer.links.map((link, i) => <span key={i}>{linkBeschriftung(link)}</span>)
            ) : (
              <span>einer anderen Stelle im System</span>
            )}
          </div>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={onBestaetigen}>
            <Ikone name="link" />
            Verknüpfen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
